import { useEffect, useRef } from 'react';

/**
 * Listens for cross-tab `storage` events on a specific localStorage key.
 * The callback is invoked with the new value whenever another tab writes to
 * the given key. Uses a ref internally so the callback can be an inline
 * function without causing the listener to be re-registered on every render.
 *
 * Note: the `storage` event fires ONLY in tabs OTHER than the one that wrote
 * the value, which is the desired behaviour for cross-tab synchronisation.
 */
export const useLocalStorageEvent = (storageEventKey: string, callback: (newValue: string | null) => void): void => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== storageEventKey) {
        return;
      }
      callbackRef.current(event.newValue);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [storageEventKey]);
};
